// Main Application Entry Point
import './firebase.js';
import { initAuth, onAuthChange, signIn, signUp, signInWithGoogle, signOut } from './auth.js';
import { showUserSettings } from './ui/userSettings.js';
import { initFeed, loadFeed } from './ui/feed.js';
import { getUserById } from './db.js';
import { initCompose } from './ui/compose.js';
import { showProfile } from './ui/profile.js';
import { showVerseSelector } from './ui/verseSelector.js';
import { getVerse, getVerses } from './bible.js';
import { MentionSystem } from './ui/mentionSystem.js';
import { NotificationsPanel } from './ui/notificationsPanel.js';
import { ReactionsBar } from './ui/reactionsBar.js';
import { CommentModal } from './ui/commentModal.js';
import { initGroups } from './ui/groups.js';
import { initDailyVerses, cleanupDailyVerses } from './ui/dailyVerses.js';
import { initPersonalNotes, cleanupPersonalNotes } from './ui/personalNotes.js';

// DOM Elements
let authModal = null;
let userMenuBtn = null;
let feedContainer = null;
let composeForm = null;
let groupsInitialized = false;
let mentionSystem = null;

/**
 * Initialize application
 */
async function initApp() {
    console.log('🚀 Initializing Social Bible Study...');

    // Get DOM elements
    feedContainer = document.querySelector('#feed');
    composeForm = document.querySelector('#compose-form');
    userMenuBtn = document.querySelector('#user-menu-btn');

    // Initialize auth
    initAuth();

    // Listen for auth state changes
    onAuthChange((user) => {
        updateUI(user);
        // Refresh feed so author controls (delete/edit) appear for the logged-in user
        if (feedContainer) {
            loadFeed();
        }
    });

    // Initialize compose form
    if (composeForm) {
        initCompose(composeForm);
        initVerseSelector();
    }

    // Initialize feed
    if (feedContainer) {
        initFeed(feedContainer);
    }

    // Tab navigation (feed ↔ groups)
    const tabNav = document.querySelector('#tab-nav');
    if (tabNav) {
        tabNav.querySelectorAll('.tab-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                tabNav.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                switchTab(btn.dataset.tab);
            });
        });
    }

    // User menu
    if (userMenuBtn) {
        userMenuBtn.addEventListener('click', showProfile);
    }

    // Login/Signup buttons
    const loginBtn = document.querySelector('#login-btn');
    const signupBtn = document.querySelector('#signup-btn');
    const logoutBtn = document.querySelector('#logout-btn');

    if (loginBtn) {
        loginBtn.addEventListener('click', () => showAuthModal('login'));
    }

    if (signupBtn) {
        signupBtn.addEventListener('click', () => showAuthModal('signup'));
    }

    if (logoutBtn) {
        logoutBtn.addEventListener('click', handleLogout);
    }

    // Settings button (new icon version)
    const settingsBtn = document.querySelector('#settings-btn');
    if (settingsBtn) {
        settingsBtn.addEventListener('click', () => showUserSettings());
    }

    // Notifications button
    const notificationsBtn = document.querySelector('#notifications-btn');
    if (notificationsBtn) {
        notificationsBtn.addEventListener('click', () => {
            const notifPanel = new NotificationsPanel();
            notifPanel.open();
        });
    }

    // Profile button
    const profileBtn = document.querySelector('#profile-btn');
    if (profileBtn) {
        profileBtn.addEventListener('click', showProfile);
    }

    // Initialize theme toggle
    initThemeToggle();

    console.log('✅ App initialized');
}

/**
 * Theme toggle (light / dark)
 */
function initThemeToggle() {
    const btn = document.querySelector('#theme-toggle-btn');
    if (!btn) return;

    // Sync icon with current theme (theme already applied by inline script in <head>)
    const current = document.documentElement.getAttribute('data-theme') || 'dark';
    btn.textContent = current === 'light' ? '☀️' : '🌙';

    btn.addEventListener('click', () => {
        const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
        const next = isDark ? 'light' : 'dark';
        document.documentElement.setAttribute('data-theme', next);
        localStorage.setItem('app_theme', next);
        btn.textContent = next === 'light' ? '☀️' : '🌙';
    });
}

/**
 * Initialize verse selector button
 */
function initVerseSelector() {
    const verseSelectorBtn = document.querySelector('#verse-selector-btn');
    const selectedVerseDisplay = document.querySelector('#selected-verse-display');
    const bookInput = document.querySelector('#post-book');
    const chapterInput = document.querySelector('#post-chapter');
    const verseStartInput = document.querySelector('#post-verse-start');
    const verseEndInput = document.querySelector('#post-verse-end');

    if (!verseSelectorBtn) return;

    verseSelectorBtn.addEventListener('click', () => {
        showVerseSelector(async (verse) => {
            // Update hidden inputs
            if (bookInput) bookInput.value = verse.book;
            if (chapterInput) chapterInput.value = verse.chapter;
            if (verseStartInput) verseStartInput.value = verse.verseStart;
            if (verseEndInput) verseEndInput.value = verse.verseEnd || verse.verseStart;

            // Display selected verse
            if (selectedVerseDisplay) {
                const verseRef = verse.verseStart === verse.verseEnd
                    ? `${verse.bookName} ${verse.chapter}:${verse.verseStart}`
                    : `${verse.bookName} ${verse.chapter}:${verse.verseStart}-${verse.verseEnd}`;

                // Show loading state
                selectedVerseDisplay.innerHTML = `
                    <div class="verse-link-display">
                        <span class="verse-info">📖 ${verseRef}</span>
                        <button type="button" class="remove-verse-btn">×</button>
                    </div>
                    <div class="verse-text-preview loading">⏳ Chargement du texte...</div>
                `;
                selectedVerseDisplay.style.display = 'block';

                try {
                    const version = localStorage.getItem('user_preferred_lang') || 'french';
                    let verseText = '';

                    if (verse.verseStart === verse.verseEnd) {
                        verseText = await getVerse(version, verse.book, verse.chapter, verse.verseStart);
                    } else {
                        const verses = await getVerses(version, verse.book, verse.chapter, verse.verseStart, verse.verseEnd);
                        verseText = verses.map(v => `<span class="verse-num">${v.verse}</span> ${v.text}`).join(' ');
                    }

                    const textPreview = selectedVerseDisplay.querySelector('.verse-text-preview');
                    if (textPreview) {
                        textPreview.classList.remove('loading');
                        textPreview.innerHTML = verseText;
                    }
                } catch (error) {
                    console.error('Error fetching verse text:', error);
                    const textPreview = selectedVerseDisplay.querySelector('.verse-text-preview');
                    if (textPreview) {
                        textPreview.innerHTML = '<span class="error">Impossible de charger le texte du verset.</span>';
                    }
                }

                // Remove verse button
                const removeBtn = selectedVerseDisplay.querySelector('.remove-verse-btn');
                removeBtn.addEventListener('click', () => {
                    if (bookInput) bookInput.value = '';
                    if (chapterInput) chapterInput.value = '';
                    if (verseStartInput) verseStartInput.value = '';
                    if (verseEndInput) verseEndInput.value = '';
                    selectedVerseDisplay.style.display = 'none';
                });
            }
        });
    });
}

/**
 * Update UI based on auth state
 */
async function updateUI(user) {
    const authButtons = document.querySelector('.auth-buttons');
    const userMenu = document.querySelector('.user-menu');
    const composeSection = document.querySelector('.compose-section');

    if (user) {
        // User is logged in
        if (authButtons) authButtons.style.display = 'none';

        const userActions = document.querySelector('#user-actions');

        if (userActions) {
            userActions.style.display = 'flex';
        }

        if (userMenu) {
            if (!userActions) userMenu.style.display = 'flex'; // Only toggle if no wrapper

            const userAvatar = userMenu.querySelector('.user-avatar');
            const userName = userMenu.querySelector('.user-name');

            // Prefer profile stored in Firestore for displayName/photoURL
            try {
                const profile = await getUserById(user.uid);
                if (userAvatar) {
                    userAvatar.src = profile.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.displayName || user.email)}`;
                }
                if (userName) {
                    userName.textContent = profile.displayName || user.email;
                }
            } catch (e) {
                if (userAvatar) {
                    userAvatar.src = user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email)}`;
                }
                if (userName) {
                    userName.textContent = user.displayName || user.email;
                }
            }
        }
        if (composeSection) composeSection.style.display = 'block';

        // Show tab navigation when logged in
        const tabNav = document.querySelector('#tab-nav');
        if (tabNav) tabNav.style.display = 'flex';
    } else {
        // User is logged out
        if (authButtons) authButtons.style.display = 'flex';

        const userActions = document.querySelector('#user-actions');
        if (userActions) userActions.style.display = 'none';

        if (userMenu) userMenu.style.display = 'none';
        if (composeSection) composeSection.style.display = 'none';

        // Hide tab nav and reset to feed tab
        const tabNav = document.querySelector('#tab-nav');
        if (tabNav) {
            tabNav.style.display = 'none';
            switchTab('feed');
        }
    }
}

/**
 * Switch between feed and groups tabs
 */
function switchTab(tab) {
    const feedSection = document.querySelector('#feed-section');
    const groupsSection = document.querySelector('#groups-section');
    const versesSection = document.querySelector('#verses-section');
    const composeSection = document.querySelector('.compose-section');

    // Hide all sections first
    if (feedSection) feedSection.style.display = 'none';
    if (groupsSection) groupsSection.style.display = 'none';
    if (versesSection) versesSection.style.display = 'none';
    const notesSection = document.querySelector('#notes-section');
    if (notesSection) notesSection.style.display = 'none';
    if (composeSection) composeSection.style.display = 'none';

    // Cleanup timers when leaving
    if (tab !== 'verses') cleanupDailyVerses();
    if (tab !== 'notes') cleanupPersonalNotes();

    if (tab === 'groups') {
        if (groupsSection) {
            groupsSection.style.display = 'block';
            if (!groupsInitialized) {
                const groupsContainer = document.querySelector('#groups-container');
                if (groupsContainer) {
                    initGroups(groupsContainer);
                    groupsInitialized = true;
                }
            }
        }
    } else if (tab === 'verses') {
        if (versesSection) {
            versesSection.style.display = 'block';
            const versesContainer = document.querySelector('#verses-container');
            if (versesContainer) initDailyVerses(versesContainer);
        }
    } else if (tab === 'notes') {
        if (notesSection) {
            notesSection.style.display = 'block';
            const notesContainer = document.querySelector('#notes-container');
            if (notesContainer) initPersonalNotes(notesContainer);
        }
    } else {
        // feed
        if (feedSection) feedSection.style.display = 'block';
        const user = document.querySelector('#user-actions')?.style.display !== 'none';
        if (composeSection) composeSection.style.display = user ? 'block' : 'none';
    }
}

/**
 * Show authentication modal
 */
function showAuthModal(mode = 'login') {
    authModal = document.createElement('div');
    authModal.className = 'modal';

    const isLogin = mode === 'login';

    authModal.innerHTML = `
    <div class="modal-content auth-modal">
      <div class="modal-header">
        <h3>${isLogin ? '🔐 Connexion' : '✨ Inscription'}</h3>
        <button class="close-btn">&times;</button>
      </div>
      <div class="modal-body">
        <form id="auth-form">
          ${!isLogin ? `
            <div class="form-group">
              <label for="auth-name">Nom</label>
              <input type="text" id="auth-name" placeholder="Votre nom" required />
            </div>
          ` : ''}
          <div class="form-group">
            <label for="auth-email">Email</label>
            <input type="email" id="auth-email" placeholder="votre@email.com" required />
          </div>
          <div class="form-group">
            <label for="auth-password">Mot de passe</label>
            <input type="password" id="auth-password" placeholder="••••••••" required minlength="6" />
          </div>
          <button type="submit" class="btn btn-primary btn-block">
            ${isLogin ? '🔓 Se connecter' : '✨ S\'inscrire'}
          </button>
        </form>
        
        <div class="divider">ou</div>
        
        <button id="google-signin-btn" class="btn btn-secondary btn-block">
          <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continuer avec Google
        </button>
        
        <div class="auth-switch">
          ${isLogin ?
            `Pas encore de compte ? <a href="#" id="switch-to-signup">S'inscrire</a>` :
            `Déjà un compte ? <a href="#" id="switch-to-login">Se connecter</a>`
        }
        </div>
      </div>
    </div>
  `;

    document.body.appendChild(authModal);

    // Close button
    authModal.querySelector('.close-btn').addEventListener('click', () => {
        authModal.remove();
    });

    // Click outside to close
    authModal.addEventListener('click', (e) => {
        if (e.target === authModal) {
            authModal.remove();
        }
    });

    // Form submission
    const form = authModal.querySelector('#auth-form');
    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const email = form.querySelector('#auth-email').value;
        const password = form.querySelector('#auth-password').value;
        const name = form.querySelector('#auth-name')?.value;

        const submitBtn = form.querySelector('button[type="submit"]');
        submitBtn.disabled = true;
        submitBtn.textContent = '⏳ Chargement...';

        try {
            if (isLogin) {
                await signIn(email, password);
            } else {
                await signUp(email, password, name);
            }

            authModal.remove();
        } catch (error) {
            console.error('Auth error:', error);

            let errorMessage = 'Une erreur est survenue';
            if (error.code === 'auth/email-already-in-use') {
                errorMessage = 'Cet email est déjà utilisé';
            } else if (error.code === 'auth/invalid-email') {
                errorMessage = 'Email invalide';
            } else if (error.code === 'auth/weak-password') {
                errorMessage = 'Mot de passe trop faible (min. 6 caractères)';
            } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
                errorMessage = 'Email ou mot de passe incorrect';
            }

            alert(errorMessage);
            submitBtn.disabled = false;
            submitBtn.textContent = isLogin ? '🔓 Se connecter' : '✨ S\'inscrire';
        }
    });

    // Google sign in
    const googleBtn = authModal.querySelector('#google-signin-btn');
    googleBtn.addEventListener('click', async () => {
        try {
            googleBtn.disabled = true;
            await signInWithGoogle();
            authModal.remove();
        } catch (error) {
            console.error('Google sign in error:', error);
            alert('Erreur lors de la connexion avec Google');
            googleBtn.disabled = false;
        }
    });

    // Switch mode
    const switchLink = authModal.querySelector(isLogin ? '#switch-to-signup' : '#switch-to-login');
    switchLink.addEventListener('click', (e) => {
        e.preventDefault();
        authModal.remove();
        showAuthModal(isLogin ? 'signup' : 'login');
    });
}

/**
 * Handle logout
 */
async function handleLogout() {
    if (!confirm('Voulez-vous vraiment vous déconnecter ?')) {
        return;
    }

    try {
        await signOut();
    } catch (error) {
        console.error('Logout error:', error);
        alert('Erreur lors de la déconnexion');
    }
}

// Start the app when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initApp);
} else {
    initApp();
}
